import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldX, Loader2, BadgeCheck } from "lucide-react";
import api from "../lib/api";
import { fmtDate } from "../lib/time";
import { Logo } from "../components/common";

export default function VerifyCV() {
  const { code } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/public/verify-cv/${code}`)
      .then((r) => { setData(r.data); setState(r.data?.valid ? "valid" : "invalid"); })
      .catch(() => setState("invalid"));
  }, [code]);

  const fmt = (iso) => fmtDate(iso);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto flex min-h-screen w-full max-w-lg flex-col px-6 py-10">
        <div className="mb-8 flex justify-center"><Logo /></div>

        {state === "loading" && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-slate-400" data-testid="verify-loading">
            <Loader2 className="h-7 w-7 animate-spin text-indigo-500" />
            Verifying certificate…
          </div>
        )}

        {state === "invalid" && (
          <div className="flex flex-1 flex-col items-center justify-center text-center" data-testid="verify-invalid">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-50"><ShieldX className="h-10 w-10 text-red-500" /></div>
            <h1 className="mt-5 font-head text-2xl font-bold text-slate-900">Certificate not found</h1>
            <p className="mt-2 text-sm text-slate-500">We couldn't verify a Servantage Service CV with code <span className="font-mono font-semibold">{code}</span>. It may have been mistyped.</p>
          </div>
        )}

        {state === "valid" && data && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="verify-valid">
            <div className="flex flex-col items-center text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50"><ShieldCheck className="h-10 w-10 text-emerald-500" /></div>
              <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-700">
                <BadgeCheck className="h-4 w-4" /> Authentic · Verified by Servantage
              </p>
              <h1 className="mt-4 font-head text-2xl font-bold text-slate-900" data-testid="verify-name">{data.name}</h1>
              <p className="text-sm font-semibold text-indigo-700">{data.job_title || "Service Professional"}</p>
              {(data.org_name || data.location) && (
                <p className="mt-1 text-sm text-slate-500">{[data.org_name, data.location].filter(Boolean).join(" · ")}</p>
              )}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Metric label="Service Index" value={data.service_index != null ? `${data.service_index}/5` : "—"} />
              <Metric label="Verified Interactions" value={data.verified_interactions ?? "—"} />
              <Metric label="Index Percentile" value={data.service_index_percent != null ? `${data.service_index_percent}%` : "—"} />
              <Metric label="Commendations" value={data.commendations ?? 0} />
            </div>

            <div className="mt-6 rounded-lg bg-slate-50 p-4 text-center text-xs text-slate-500">
              Verification code <span className="font-mono font-bold text-slate-700">{data.code}</span><br />
              Issued {fmt(data.issued_at)}
            </div>
            <p className="mt-4 text-center text-xs text-slate-400">
              Every metric is drawn from real, anti-gaming-screened customer feedback on the Servantage platform.
            </p>
          </div>
        )}

        <div className="mt-8 text-center text-xs text-slate-400">
          <Link to="/" className="underline hover:text-slate-600">About Servantage</Link>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 text-center">
      <div className="font-head text-2xl font-bold text-slate-900">{value}</div>
      <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}
